import React from 'react';
import Helmet from 'react-helmet';
import { useTranslation } from '@wix/app-market-core';
import type { AppReviewsSummaryType } from '@wix/app-market-services';

export interface AppPageSEOProps {
  description: string;
  metaTagsAggregator: string[];
  appName: string;
  reviewsSummary: AppReviewsSummaryType;
  icon: string;
  hiddenAppCollections: {
    slug: string;
    name: string;
  }[];
}

export function AppPageSEO({
  description,
  metaTagsAggregator,
  appName,
  reviewsSummary,
  icon,
  hiddenAppCollections,
}: AppPageSEOProps) {
  const { t } = useTranslation();

  function trimQuotes(value: string): string {
    return value ? value.replace(/"/g, '') : '';
  }
  if (metaTagsAggregator) {
    metaTagsAggregator.push(
      `<title data-meta-tags-aggregator="true">${t('solution-page.seo.title', {
        appName,
      })}</title>`,
    );
    metaTagsAggregator.push(
      `<meta data-meta-tags-aggregator="true" property="og:title" content="${t(
        'solution-page.seo.title',
        {
          appName,
        },
      )}" />`,
    );
    metaTagsAggregator.push(
      `<meta data-meta-tags-aggregator="true" property="description" content="${description}" />`,
    );
    metaTagsAggregator.push(
      `<meta data-meta-tags-aggregator="true" property="og:description" content="${description}" />`,
    );
    hiddenAppCollections.find(
      (hiddenAppCollection) => hiddenAppCollection.slug === 'no-index-apps',
    ) && metaTagsAggregator.push(`<meta name="robots" content="noindex" />`);
  }
  return (
    <Helmet>
      <title>
        {t('solution-page.seo.title', {
          appName,
        })}
      </title>
      <meta name="description" content={description} />
      <meta
        property="og:title"
        content={t('solution-page.seo.title', {
          appName,
        })}
      />
      <meta property="og:description" content={description} />
      <script type="application/ld+json">{`{
              "@context": "https://schema.org/",
              "@type": "SoftwareApplication",
              "name": "${trimQuotes(appName)}",${
        reviewsSummary.totalReviews > 0 && reviewsSummary.averageRating > 0
          ? `
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": ${reviewsSummary.averageRating},
                "ratingCount": ${reviewsSummary.totalReviews}
              },`
          : ''
      }
              "applicationCategory": "DeveloperApplication",
              "description": "${trimQuotes(description)}",
              "image": "${icon}"
            }`}</script>
    </Helmet>
  );
}
